import React, { useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Icon, IconVariant } from "src/design-system/Icon";

/** Decorative adornments render inside the tab's click target. */
export type DecorativeTabSuffix =
  | { type: "icon"; icon: IconProp; variant?: IconVariant }
  | {
      type: "pill";
      label: React.ReactNode;
      variant?: PillVariants;
      icon?: IconProp;
    };

/** The menu is interactive, so it renders beside the tab — never inside it. */
export type TabSuffix =
  | DecorativeTabSuffix
  | {
      type: "ellipsis";
      elements: OptionsDropdownElement[];
      icon?: IconProp;
      dataLoc?: string;
    };

export type TabItem = {
  key: string;
  label: React.ReactNode;
  /** Panel content shown when the tab is active. */
  content?: React.ReactNode;
  prefixIcon?: IconProp;
  suffix?: TabSuffix;
  dataLoc?: string;
};

type ControlledProps = {
  /** Active tab key. Provide together with `onChange`. */
  selectedKey: string;
  onChange: (key: string) => void;
  defaultSelectedKey?: never;
};

type UncontrolledProps = {
  /** Initially active tab key. Defaults to the first tab. */
  defaultSelectedKey?: string;
  selectedKey?: never;
  onChange?: never;
};

export type TabsProps = (ControlledProps | UncontrolledProps) & {
  tabs: TabItem[];
  /** Unmount inactive panels. Defaults to `true`. */
  unmountPanels?: boolean;
  dataLoc?: string;
};

const TabSuffixAdornment: React.FC<{ suffix: TabSuffix }> = ({ suffix }) => {
  switch (suffix.type) {
    case "icon":
      return <Icon icon={suffix.icon} size="xs" variant={suffix.variant} />;
    case "pill":
      return (
        <Pill size="sm" variant={suffix.variant ?? "gray"}>
          {suffix.icon && <Pill.Icon icon={suffix.icon} />}
          <Pill.Text>{suffix.label}</Pill.Text>
        </Pill>
      );
    case "ellipsis":
      return (
        <EllipsisOptionsDropdown
          dataLoc={suffix.dataLoc}
          elements={suffix.elements}
          icon={suffix.icon}
        />
      );
  }
};

/** Prefix icon + label + decorative suffix: the inner row shared by the interactive `Tab` and the presentational `Tabs.Trigger`. */
const TabFace: React.FC<{
  label: React.ReactNode;
  prefixIcon?: IconProp;
  suffix?: DecorativeTabSuffix;
}> = ({ label, prefixIcon, suffix }) => (
  <>
    {prefixIcon && <Icon icon={prefixIcon} size="xs" />}
    <span className="whitespace-nowrap font-inter-semibold-13px text-gray-800">
      {label}
    </span>
    {suffix && <TabSuffixAdornment suffix={suffix} />}
  </>
);

/**
 * Shared geometry: stacked content row + full-width indicator below. The
 * `adjacent` slot holds an interactive control (the actions menu) that sits
 * beside — never inside — the tab's click target; decorative icon/pill
 * adornments live inside the trigger so the whole face activates the tab.
 */
const TabShell: React.FC<{
  children: React.ReactNode;
  adjacent?: React.ReactNode;
  active: boolean;
  dataLoc?: string;
}> = ({ children, adjacent, active, dataLoc }) => (
  <div className="flex flex-col items-center gap-2.5" data-loc={dataLoc}>
    <div className="flex items-center gap-1.5 px-1">
      {children}
      {adjacent}
    </div>
    <span
      className={twJoin(
        "h-1 w-full rounded-full",
        active ? "bg-indigo-600" : "bg-transparent",
      )}
    />
  </div>
);

type TabTriggerProps = {
  label: React.ReactNode;
  prefixIcon?: IconProp;
  /** Non-interactive adornment only — interactive menus are composed as a sibling control. */
  suffix?: DecorativeTabSuffix;
  isActive?: boolean;
  dataLoc?: string;
};

/**
 * The presentational tab face: prefix icon + label + non-interactive suffix +
 * 3px indicator, driven by `isActive`. It is **not** interactive — wrap it in
 * your own `<button>` / `NavLink` (which owns focus, role, and click), and place
 * it inside a caller-owned `<nav>` / `role="tablist"` that draws the
 * `border-b border-gray-200` baseline.
 */
const TabTrigger: React.FC<TabTriggerProps> = ({
  label,
  prefixIcon,
  suffix,
  isActive = false,
  dataLoc,
}) => (
  <TabShell active={isActive} dataLoc={dataLoc}>
    <TabFace label={label} prefixIcon={prefixIcon} suffix={suffix} />
  </TabShell>
);

export const Tabs: React.FC<TabsProps> & {
  Trigger: typeof TabTrigger;
} = (props) => {
  const { tabs, unmountPanels = true, dataLoc } = props;

  const [internalKey, setInternalKey] = useState(
    props.defaultSelectedKey ?? tabs[0]?.key ?? "",
  );

  // Controlled when a `selectedKey` is supplied, mirroring `Accordion`.
  const selectedKey = props.selectedKey ?? internalKey;
  const handleChange =
    props.selectedKey !== undefined ? props.onChange : setInternalKey;

  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === selectedKey),
  );

  return (
    <TabGroup
      as="div"
      data-loc={dataLoc}
      selectedIndex={selectedIndex}
      onChange={(index) => handleChange(tabs[index].key)}
    >
      <TabList className="scrollbar-hide flex items-start gap-4 overflow-x-auto border-gray-200 border-b">
        {tabs.map((tab, index) => {
          // The actions menu is interactive, so it sits beside the tab and
          // never selects it. Icon/pill adornments ride inside the button.
          const ellipsis =
            tab.suffix?.type === "ellipsis" ? tab.suffix : undefined;
          const adornment =
            tab.suffix?.type === "ellipsis" ? undefined : tab.suffix;
          return (
            <TabShell
              key={tab.key}
              active={index === selectedIndex}
              adjacent={ellipsis && <TabSuffixAdornment suffix={ellipsis} />}
              dataLoc={tab.dataLoc ?? tab.key}
            >
              <Tab className="flex items-center gap-1.5 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/25">
                <TabFace
                  label={tab.label}
                  prefixIcon={tab.prefixIcon}
                  suffix={adornment}
                />
              </Tab>
            </TabShell>
          );
        })}
      </TabList>
      <TabPanels>
        {tabs.map((tab) => (
          <TabPanel
            key={tab.key}
            as="div"
            data-loc={`${tab.key}-panel`}
            unmount={unmountPanels}
          >
            {tab.content}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
};

Tabs.Trigger = TabTrigger;
